import { PushSessionBodyParams } from "@authory-packages/core";
import { createSmartShareSession, refreshSmartShareSession, smartShareSessionPush } from "../api/smart-share";

const STORAGE_KEY = "smart_tracking_sessions";

type SessionMap = Record<string, string | null>;

function readSessionMap(): SessionMap {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function writeSessionMap(map: SessionMap): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

export class SmartSessionTracker {
    private smartShareId: string;
    private userSlug: string;
    private sessionId: string | null = null;

    constructor(smartShareId: string, userSlug: string) {
        this.smartShareId = smartShareId;
        this.userSlug = userSlug;

        this.init();
    }

    // Step 1: Call this once after constructing, e.g. in a useEffect
    async init(): Promise<void> {
        const map = readSessionMap();

        if (!(this.smartShareId in map)) {
            // First time we see this URL — initialise with null so the key exists
            map[this.smartShareId] = null;
            writeSessionMap(map);
        }

        const savedSessionId = map[this.smartShareId];

        if (savedSessionId) {
            const valid = await this.refreshSession(savedSessionId);
            if (valid) {
                this.sessionId = savedSessionId;
                return;
            }
        }

        // No session or session expired (409) — create a fresh one
        await this.createSession();
    }

    // Step 2: Track an article view. Creates a new session automatically on 409.
    async trackArticle(articleId: string): Promise<void> {
        const success = await this.sendTrackingEvent(articleId);

        if (!success) {
            // Session was rejected — create a new one and retry once
            await this.createSession();
            await this.sendTrackingEvent(articleId);
        }
    }

    // ── private helpers ────────────────────────────────────────────────────────

    /** Returns true if the session is still valid, false on 409 or any error. */
    private async refreshSession(sessionId: string): Promise<boolean> {
        try {
            const res = await refreshSmartShareSession({ userSlug: this.userSlug, smartShareId: this.smartShareId, sessionId });
            return res.status !== 409;
        } catch {
            return false;
        }
    }

    /** Calls the create endpoint, persists the new session id, and updates state. */
    private async createSession(): Promise<void> {
        try {
            const res = await createSmartShareSession({ userSlug: this.userSlug, smartShareId: this.smartShareId });
            if (res.status !== 200) return;

            this.sessionId = res.data.uid as string;

            const map = readSessionMap();
            map[this.smartShareId] = this.sessionId;
            writeSessionMap(map);
        } catch {
            // Swallow — tracking should never break the page
        }
    }

    /**
     * Sends a tracking event.
     * Returns true on success, false when the server responds with 409
     * (stale session) so the caller can refresh and retry.
     */
    private async sendTrackingEvent(articleId: string): Promise<boolean> {
        if (!this.sessionId) return false;

        try {
            const payload = new PushSessionBodyParams();
            payload.article = articleId;

            const res = await smartShareSessionPush({ userSlug: this.userSlug, smartShareId: this.smartShareId, sessionId: this.sessionId, payload });

            return res.status !== 409;
        } catch {
            return false;
        }
    }
}
